/* eslint-disable boundaries/element-types */
import { AddressAutofillRetrieveResponse } from '@mapbox/search-js-core'
import { AddressAutofill } from '@mapbox/search-js-react'
import { useRuntimeVariables } from 'app-kit'
import React, { useEffect, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { GeckoAddress } from 'types'

import {
  StyledInputContainer,
  StyledLabel,
  TextInputProps,
  TextInputStyled,
  TextValue,
} from './InputStyles'

export interface AddressInputProps extends TextInputProps {
  formSetValue: UseFormSetValue<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
    [x: string]: any
  }>
  formItemKey: string
  formatAddressValueAsString?: boolean
}

export const AddressInput = React.forwardRef<
  HTMLInputElement,
  AddressInputProps
>((props, ref) => {
  const {
    $label,
    $labelColor,
    $labelLocation,
    mode,
    formSetValue,
    formItemKey,
    defaultValue,
    formatAddressValueAsString = true,
    ...inputProps
  } = props

  const { MAPBOX_KEY } = useRuntimeVariables()
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (typeof defaultValue === 'string') {
      setInputValue(defaultValue)
    }
  }, [defaultValue])

  const onRetrieve = (response: AddressAutofillRetrieveResponse) => {
    const address1 = response.features[0].properties.address_line1
    const address2 = response.features[0].properties.address_line2
    const city = response.features[0].properties.address_level2
    const state = response.features[0].properties.address_level1
    const zip = response.features[0].properties.postcode
    const country = response.features[0].properties.country
    const combinedAddress =
      `${address1}, ${address2}, ${city}, ${state}, ${zip}, ${country}`
        .replace(/(, )+/g, ', ')
        .replace(/^, |, $/g, '')

    if (formatAddressValueAsString) {
      formSetValue(formItemKey, combinedAddress)
    } else {
      const addressObject: GeckoAddress = {
        address_line_1: address1 || '',
        address_line_2: address2,
        city: city || '',
        region: state || '',
        postal_code: zip || '',
        country: country || '',
      }
      formSetValue(formItemKey, addressObject)
    }

    setInputValue(combinedAddress)
  }

  return (
    <StyledInputContainer $labelLocation={$labelLocation}>
      {$label && (
        <StyledLabel $labelColor={$labelColor} htmlFor={$label}>
          {$label}
        </StyledLabel>
      )}
      {/* This component expects React 16 types. It's confused */}
      {/* @ts-expect-error AddressAutofill Component */}
      <AddressAutofill accessToken={MAPBOX_KEY} onRetrieve={onRetrieve}>
        {mode === 'read' ? (
          <TextValue>{defaultValue}</TextValue>
        ) : (
          <TextInputStyled
            {...inputProps}
            ref={ref}
            placeholder='Type an address'
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
      </AddressAutofill>
    </StyledInputContainer>
  )
})

AddressInput.displayName = 'AddressInput'
